import { Tooltip, TooltipContent, TooltipTrigger } from '@workspace/ui/components/tooltip'
import { ChevronDown, Info } from 'lucide-react'
import { useMemo, useState } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts'
import type { FounderProfileAnalyticsData } from './types'

const TOOLTIP_STYLE = {
  background: '#1a1a1a',
  border: '1px solid #333',
  borderRadius: 8,
  fontSize: 11,
}

/**
 * Zone 2's deck-reading chart. Renders exactly ONE deck version at a time —
 * the version picker switches which version's `pages[]` is shown, never
 * blends them. See the plan's "per-version isolation" requirement.
 */
export function DeckReadingChart({
  deckReading,
}: {
  deckReading: FounderProfileAnalyticsData['deckReading']
}) {
  const [selectedId, setSelectedId] = useState(deckReading[0]?.documentId)
  const version = deckReading.find((d) => d.documentId === selectedId) ?? deckReading[0]

  const dropOff = useMemo(() => {
    if (!version || version.pages.length < 2) return null
    const baseReach = version.pages[0]?.reachCount ?? 0
    if (baseReach === 0) return null
    // The cumulative stop-% (relative to page 1) is monotonically
    // non-decreasing and necessarily tops out at 100% wherever the data
    // simply runs out — picking the page with the highest cumulative value
    // would always just report that tail, even when most readers actually
    // left much earlier in one sharp step. So this picks the page with the
    // biggest single-step INCREASE in that cumulative stop-%, i.e. the real
    // cliff, not wherever the curve happens to end.
    let worst: { page: number; pct: number; delta: number } | null = null
    let prevStopPct = 0
    for (let i = 1; i < version.pages.length; i++) {
      const page = version.pages[i]
      if (!page) continue
      const stopPct = Math.round((1 - page.reachCount / baseReach) * 100)
      const delta = stopPct - prevStopPct
      if (!worst || delta > worst.delta) worst = { page: page.page, pct: stopPct, delta }
      prevStopPct = stopPct
    }
    return worst && worst.delta >= 20 ? worst : null
  }, [version])

  const hasAnyData = deckReading.some((d) => d.pages.length > 0)
  if (!version || !hasAnyData) return null

  return (
    <div className="rounded-xl border border-border bg-card/30 px-4 py-3.5">
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <p className="flex items-center gap-1.5 text-[13.5px] font-semibold text-foreground">
          How far investors read your deck
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="size-3 shrink-0 text-muted-foreground/60" />
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-64">
              Average seconds spent on each deck page, and where readers stop.
            </TooltipContent>
          </Tooltip>
        </p>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-muted-foreground">avg seconds per page</span>
          {deckReading.length > 1 ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="relative">
                  <select
                    value={version.documentId}
                    onChange={(e) => setSelectedId(e.target.value)}
                    className="appearance-none rounded-full border border-border bg-card2 py-1 pl-2.5 pr-6 text-[11.5px] font-semibold text-foreground"
                  >
                    {deckReading.map((d) => (
                      <option key={d.documentId} value={d.documentId}>
                        Deck {d.versionLabel}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-1.5 top-1/2 size-3 -translate-y-1/2 text-muted-foreground" />
                </div>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-64">
                Numbers for the deck version live right now. Earlier versions keep their own
                numbers; pick one to see whether your edit worked.
              </TooltipContent>
            </Tooltip>
          ) : (
            // Only one version exists — nothing to switch between, but still
            // say which one this is instead of leaving the label blank.
            <span className="rounded-full border border-border bg-card2 py-1 pl-2.5 pr-2.5 text-[11.5px] font-semibold text-foreground">
              Deck {version.versionLabel}
            </span>
          )}
        </div>
      </div>

      {version.pages.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          No deck reading recorded for this version yet.
        </p>
      ) : (
        <div className="relative">
          {dropOff && (
            <p className="absolute right-1 top-1 z-10 rounded-md border border-red-400/30 bg-card px-2 py-0.5 text-[10.5px] text-red-400">
              {dropOff.pct}% stop after page {dropOff.page}
            </p>
          )}
          <ResponsiveContainer width="100%" height={140}>
            <BarChart
              data={version.pages}
              margin={{ top: 4, right: 0, bottom: 0, left: -24 }}
              barCategoryGap="20%"
            >
              <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="#2a2a2a" />
              <XAxis
                dataKey="page"
                tick={{ fontSize: 9, fill: '#666' }}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                tick={{ fontSize: 9, fill: '#555' }}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v: number) => `${v}s`}
              />
              {dropOff && (
                <ReferenceLine
                  x={dropOff.page}
                  stroke="#f87171"
                  strokeDasharray="4 4"
                  strokeOpacity={0.6}
                />
              )}
              <Bar dataKey="avgSeconds" fill="#f97316" radius={[2, 2, 0, 0]} />
              <RechartsTooltip
                contentStyle={TOOLTIP_STYLE}
                cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                formatter={(value) => [`${value}s`, 'Avg time']}
                labelFormatter={(label) => `Page ${label}`}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  )
}
